<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="1200px"
    scrollable
    persistent
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <div>
          {{ channelName }}
          <small class="text-medium-emphasis ml-2">({{ totalMessages }} сообщений)</small>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="closeModal"
        ></v-btn>
      </v-card-title>
      
      <v-card-text class="pa-0" style="height: 70vh;">
        <div class="messages-container" ref="messagesContainer" @scroll="onScroll">
          <div v-if="isLoading && messages.length === 0" class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <div class="mt-2">Загрузка...</div>
          </div>
          
          <div v-if="messages.length === 0 && !isLoading" class="text-center pa-4 text-medium-emphasis">
            Сообщения не найдены
          </div>
          
          <div v-for="message in messages" :key="message.message_id" class="message-item">
            <v-row>
              <!-- Дата и ссылка 'Оригинал' -->
              <v-col cols="12" md="3">
                <div class="message-date">{{ formatTimestamp(message.timestamp) }}</div>
                <div class="message-link">
                  <a :href="getMessageUrl(message.message_id)" target="_blank" class="text-primary">
                    Оригинал
                    <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                  </a>
                </div>
              </v-col>

              <!-- Текст сообщения -->
              <v-col cols="12" md="9">
                <div class="message-content">
                  <!-- Channel header: avatar + title + @handle -->
                  <div v-if="cleanUsername" class="author-header mb-2">
                    <v-avatar size="32" class="mr-2">
                      <v-img v-if="thumbnailEncoded" :src="getImageSrc(thumbnailEncoded)" cover></v-img>
                      <v-icon v-else color="grey-lighten-1">mdi-account</v-icon>
                    </v-avatar>
                    <div class="d-flex flex-column">
                      <span class="author-name">{{ channelName }}</span>
                      <a
                        :href="`https://t.me/${cleanUsername}`"
                        target="_blank"
                        rel="noopener"
                        class="author-handle"
                      >@{{ cleanUsername }}</a>
                    </div>
                  </div>
                  <!-- Forwarded from информация -->
                  <div v-if="message.forwarded_from_name" class="forwarded-from">
                    <v-icon size="small" class="text-medium-emphasis mr-1">mdi-share</v-icon>
                    <span class="text-medium-emphasis">Forwarded from</span>
                    <a v-if="message.forwarded_from_url" 
                       :href="message.forwarded_from_url" 
                       target="_blank" 
                       class="forwarded-channel-link ml-1">
                      {{ message.forwarded_from_name }}
                    </a>
                    <span v-else class="forwarded-channel-name ml-1">
                      {{ message.forwarded_from_name }}
                    </span>
                  </div>
                  
                  <!-- Reply text если есть -->
                  <div v-if="message.reply_text" class="reply-text">
                    <a 
                      v-if="message.reply_message_id"
                      :href="getReplyUrl(message)"
                      target="_blank"
                      class="reply-link"
                    >
                      <v-icon size="small" class="text-medium-emphasis mr-2">mdi-reply</v-icon>
                      <span class="text-medium-emphasis font-italic">{{ message.reply_text }}</span>
                    </a>
                    <div v-else>
                      <v-icon size="small" class="text-medium-emphasis mr-2">mdi-reply</v-icon>
                      <span class="text-medium-emphasis font-italic">{{ message.reply_text }}</span>
                    </div>
                  </div>
                  
                  <!-- Основной текст -->
                  <div v-if="message.text" class="main-text" v-html="formatMessageText(message.text, message.links)">
                  </div>
                  
                  <!-- Медиа placeholder -->
                  <div v-if="message.has_media || (message.images && message.images.length > 0)" class="media-placeholder">
                    <div class="media-placeholder-content">
                      <v-icon class="text-medium-emphasis mr-2">mdi-image</v-icon>
                      <span class="text-medium-emphasis">Медиа (Изображение/Видео)</span>
                    </div>
                  </div>
                  
                  <!-- Ссылки из сообщения -->
                  <div v-if="getFilteredLinks(message.links, message.message_id).length > 0" class="message-links">
                    <div class="links-header">
                      <v-icon size="small" class="text-medium-emphasis mr-1">mdi-link</v-icon>
                      <small class="text-medium-emphasis">Ссылки:</small>
                    </div>
                    <div class="links-list">
                      <a 
                        v-for="(link, index) in getFilteredLinks(message.links, message.message_id)" 
                        :key="index"
                        :href="link" 
                        target="_blank" 
                        class="link-item"
                      >
                        {{ formatLinkDisplay(link) }}
                        <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                      </a>
                    </div>
                  </div>
                  
                  <!-- Views с иконкой глаза -->
                  <div v-if="message.views > 0" class="message-views">
                    <v-icon size="small" class="text-medium-emphasis mr-1">mdi-eye</v-icon>
                    <small class="text-medium-emphasis">{{ formatNumber(message.views) }}</small>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
          
          <!-- Индикатор загрузки при бесконечном скроллинге -->
          <div v-if="isLoading && messages.length > 0" class="text-center pa-3">
            <v-progress-circular
              indeterminate
              color="primary"
              size="small"
            ></v-progress-circular>
          </div>
          
          <!-- Сообщение о том, что все сообщения загружены -->
          <div v-if="!hasMore && messages.length > 0" class="text-center pa-3 text-medium-emphasis">
            <small>Все сообщения загружены</small>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { QUOTE_HTML, dropLeftoverMarkers } from '@/utils/messageMarkers';
// import apiClient from "@/apiClient"; // Not used, using direct fetch instead

export default {
  name: "MessagesModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    channelName: {
      type: String,
      required: true
    },
    telegramUsername: {
      type: String,
      required: true
    },
    thumbnailEncoded: {
      type: String,
      default: ''
    }
  },
  emits: ["close", "show-message"],
  data() {
    return {
      messages: [],
      isLoading: false,
      hasMore: true,
      totalMessages: 0,
      currentOffset: 0,
      limit: 20
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.isVisible;
      },
      set(value) {
        if (!value) {
          this.closeModal();
        }
      }
    },
    cleanUsername() {
      const u = this.telegramUsername || '';
      return u.startsWith('@') ? u.substring(1) : u;
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetData();
        this.loadMessages();
        document.addEventListener('keydown', this.handleEscape);
      } else {
        document.removeEventListener('keydown', this.handleEscape);
      }
    }
  },
  methods: {
    resetData() {
      this.messages = [];
      this.currentOffset = 0;
      this.hasMore = true;
      this.totalMessages = 0;
    },
    
    async loadMessages() {
      if (this.isLoading || !this.hasMore) return;
      
      this.isLoading = true;
      
      try {
        // tracker-data работает на порту 8030
        const dataServiceUrl = process.env.VUE_APP_DATA_SERVICE_URL || 'http://localhost:8030';
        // Убираем @ из имени канала если он есть для API запроса
        const cleanUsername = this.telegramUsername.startsWith('@') 
          ? this.telegramUsername.substring(1) 
          : this.telegramUsername;
        const response = await fetch(
          `${dataServiceUrl}/api/telegram/messages/${encodeURIComponent(cleanUsername)}?offset=${this.currentOffset}&limit=${this.limit}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (this.currentOffset === 0) {
          this.messages = data.items;
          this.totalMessages = data.total;
        } else {
          this.messages.push(...data.items);
        }
        
        this.currentOffset += this.limit;
        this.hasMore = data.items.length === this.limit && this.messages.length < data.total;
        
      } catch (error) {
        console.error("Ошибка при загрузке сообщений:", error);
        this.$emit('show-message', 'Ошибка при загрузке сообщений', true);
      } finally {
        this.isLoading = false;
      }
    },
    
    onScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      
      // Проверяем, достиг ли пользователь нижней части контейнера
      const threshold = 100; // пикселей до конца
      const position = container.scrollTop + container.clientHeight;
      const height = container.scrollHeight;
      
      if (position >= height - threshold && this.hasMore && !this.isLoading) {
        this.loadMessages();
      }
    },
    
    getImageSrc(encoded) {
      if (!encoded) return '';
      // thumbnail_encoded is stored as base64 in the DB; accept raw base64
      // or already-prefixed data URLs.
      // an address of our store (/api/sources/{id}/avatar) or any URL goes as is
      if (encoded.startsWith('data:') || encoded.startsWith('/') || encoded.startsWith('http')) return encoded;
      return `data:image/jpeg;base64,${encoded}`;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      
      try {
        const date = new Date(timestamp);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const HH = String(date.getHours()).padStart(2, '0');
        const MM = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        return `${dd}.${mm}.${yyyy} ${HH}:${MM}:${ss}`;
      } catch (e) {
        return timestamp;
      }
    },
    
    getMessageUrl(messageId) {
      // Убираем @ из имени канала если он есть
      const cleanUsername = this.telegramUsername.startsWith('@') 
        ? this.telegramUsername.substring(1) 
        : this.telegramUsername;
      return `https://t.me/s/${cleanUsername}/${messageId}`;
    },
    
    getReplyUrl(message) {
      // Если есть reply_channel_name, используем его, иначе текущий канал
      const replyChannel = message.reply_channel_name || this.telegramUsername.replace('@', '');
      return `https://t.me/${replyChannel}/${message.reply_message_id}`;
    },
    
    formatNumber(value) {
      if (value == null) return '';
      return value.toLocaleString('ru-RU');
    },
    
    formatMessageText(text) {
      if (!text) return '';
      
      let formattedText = text;
      // const currentChannel = this.telegramUsername.replace('@', ''); // Not used in formatting
      
      
      // Экранируем HTML символы
      formattedText = formattedText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      
      // Сначала обрабатываем старые маркеры из базы данных (для обратной совместимости)
      formattedText = formattedText.replace(/___LINE_BREAK___/g, '\n');
      formattedText = formattedText.replace(/___MENTION_\d+___/g, '');
      formattedText = formattedText.replace(/_LINE_BREAK_/g, '\n');
      formattedText = formattedText.replace(/__MENTION_\d+__MENTION_/g, '');
      
      // Сохраняем переносы строк до начала обработки
      const lineBreaks = [];
      let lineBreakIndex = 0;
      formattedText = formattedText.replace(/\n/g, () => {
        const marker = `{{LINEBREAK${lineBreakIndex}}}`;
        lineBreaks.push(marker);
        lineBreakIndex++;
        return marker;
      });
      
      // Используем временные маркеры для защиты от повторной обработки
      const mentionMarker = '{{MENTION}}';
      const linkMarker = '{{LINKMARKER}}';
      const processedMentions = [];
      const processedLinks = [];
      
      // 1. Обрабатываем маркеры ссылок {{LINK:url|text}}
      formattedText = formattedText.replace(
        /\{\{LINK:([^|]+)\|([^}]+)\}\}/g,
        (match, url, linkText) => {
          // Определяем тип ссылки
          let linkClass = 'message-link';
          if (url.includes('t.me/')) {
            linkClass = 'channel-link';
          } else if (url.startsWith('http')) {
            linkClass = 'external-link';
          }
          
          const linkHtml = `<a href="${url}" target="_blank" class="${linkClass}">${linkText}</a>`;
          processedLinks.push(linkHtml);
          return linkMarker + (processedLinks.length - 1) + linkMarker;
        }
      );
      
      // 2. Обрабатываем упоминания @username (исключаем email адреса)
      formattedText = formattedText.replace(
        /(?<![a-zA-Z0-9._])@([a-zA-Z0-9_]{1,32})(?![a-zA-Z0-9._])/g,
        (match, username) => {
          const linkHtml = `<a href="https://t.me/${username}" target="_blank" class="mention-link">@${username}</a>`;
          processedMentions.push(linkHtml);
          return mentionMarker + (processedMentions.length - 1) + mentionMarker;
        }
      );
      
      // 3. Обрабатываем код `text`
      formattedText = formattedText.replace(/`([^`]+)`/g, '<code>$1</code>');
      
      // 4. Обрабатываем подчеркивание __text__
      formattedText = formattedText.replace(/__([^_]+)__/g, '<u>$1</u>');
      
      // 5. Обрабатываем жирный текст **text**
      formattedText = formattedText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
      
      // 6. Обрабатываем курсив *text*
      formattedText = formattedText.replace(/\*([^*]+)\*/g, '<i>$1</i>');
      
      // 7. Восстанавливаем обработанные ссылки
      formattedText = formattedText.replace(new RegExp(linkMarker + '(\\d+)' + linkMarker, 'g'), (match, index) => {
        return processedLinks[parseInt(index)];
      });
      
      // 8. Восстанавливаем обработанные упоминания
      formattedText = formattedText.replace(new RegExp(mentionMarker + '(\\d+)' + mentionMarker, 'g'), (match, index) => {
        return processedMentions[parseInt(index)];
      });
      
      // 9. Восстанавливаем переносы строк ПЕРЕД обработкой блок-цитат
      lineBreaks.forEach((marker) => {
        formattedText = formattedText.replace(marker, '<br>');
      });
      
      // 10. Обрабатываем блок-цитаты {{BLOCKQUOTE:text}} ПОСЛЕ восстановления переносов
      formattedText = formattedText.replace(QUOTE_HTML, '<blockquote class="message-blockquote">$1</blockquote>');

      return dropLeftoverMarkers(formattedText);
    },
    
    getFilteredLinks(links, currentMessageId) {
      if (!links || !Array.isArray(links)) return [];
      
      const currentChannel = this.telegramUsername.replace('@', '');
      
      return links.filter(link => {
        // Убираем пустые ссылки или только пробелы
        if (!link || link.trim() === '') {
          return false;
        }
        
        // Проверяем ссылки на свой канал
        const isSelfChannelLink = link.includes(`t.me/s/${currentChannel}`) || 
                                  link.includes(`t.me/${currentChannel}`) ||
                                  link.includes(`@${currentChannel}`) ||
                                  link.includes(`/${currentChannel}/`);
        
        if (isSelfChannelLink) {
          // Если это ссылка на конкретное сообщение своего канала (содержит /цифры в конце)
          const messageIdMatch = link.match(new RegExp(`/${currentChannel}/(\\d+)/?$`));
          if (messageIdMatch) {
            const linkMessageId = messageIdMatch[1];
            // Скрываем ссылку на текущее сообщение
            if (linkMessageId === currentMessageId) {
              return false;
            }
            // Показываем ссылки на другие сообщения своего канала
            return true;
          } else {
            // Скрываем общие ссылки на канал (без номера сообщения)
            return false;
          }
        }
        
        // Оставляем все остальные ссылки
        return true;
      });
    },
    
    formatLinkDisplay(link) {
      // Сокращаем длинные ссылки для отображения
      if (link.length > 50) {
        return link.substring(0, 47) + '...';
      }
      return link;
    },
    
    closeModal() {
      this.$emit('close');
    },
    
    handleEscape(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    }
  },
  
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
  }
};
</script>

<style scoped>
.messages-container {
  height: 70vh;
  overflow-y: auto;
  padding: 1rem;
}

.author-header {
  display: flex;
  align-items: center;
}
.author-name {
  font-weight: 600;
  line-height: 1.1;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.95rem;
}
.author-handle {
  font-size: 0.8rem;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.author-handle:hover {
  text-decoration: underline;
}

.message-item {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
}

.message-item:last-child {
  border-bottom: none;
}

.message-date {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9rem;
}

.message-link {
  margin-top: 0.25rem;
}

.message-link a {
  text-decoration: none;
  font-size: 0.85rem;
}

.message-link a:hover {
  text-decoration: underline;
}

.message-content {
  line-height: 1.5;
}

.forwarded-from {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
}

.forwarded-channel-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
}

.forwarded-channel-link:hover {
  color: rgb(var(--v-theme-primary-darken-1));
  text-decoration: underline;
}

.forwarded-channel-name {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
}

.reply-text {
  background-color: rgba(var(--v-theme-surface-variant), 0.1);
  border-left: 3px solid rgb(var(--v-theme-on-surface-variant));
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.7) !important;
}

.reply-link {
  display: block;
  text-decoration: none;
  color: rgba(var(--v-theme-on-surface), 0.7) !important;
  transition: background-color 0.2s ease;
}

.reply-link:hover {
  background-color: rgb(var(--v-theme-surface-bright));
  margin: -0.5rem -0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
}

.main-text {
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Стили для blockquote */
.main-text :deep(blockquote), 
.main-text :deep(.message-blockquote) {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  border-left: 4px solid rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-surface-variant));
  font-style: italic;
  color: rgba(var(--v-theme-on-surface-variant), 0.7);
}

/* Стили для code */
.main-text :deep(code) {
  background-color: rgb(var(--v-theme-surface-variant));
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875em;
  color: rgb(var(--v-theme-error));
}

/* Стили для подчеркнутого текста */
.main-text :deep(u) {
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-underline-offset: 0.125rem;
}

/* Стили для медиа placeholder */
.media-placeholder {
  margin: 0.75rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  text-align: center;
}

.media-placeholder-content {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
}

.media-placeholder-content i {
  font-size: 1.25rem;
}

.message-views {
  margin-top: 0.5rem;
  text-align: right;
}

/* Стилизация скроллбара */
.messages-container::-webkit-scrollbar {
  width: 8px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Стили для ссылок в тексте сообщений */
.main-text :deep(a) {
  text-decoration: none;
  transition: all 0.2s ease;
}

.main-text :deep(a:hover) {
  text-decoration: underline;
}

/* Обычные ссылки в тексте */
.main-text :deep(.message-link) {
  color: #0066cc;
  font-weight: 500;
}

.main-text :deep(.message-link:hover) {
  color: #0052cc;
}

/* Упоминания @username */
.main-text :deep(.mention-link) {
  color: #1da1f2;
  font-weight: 500;
}

.main-text :deep(.mention-link:hover) {
  color: #0d8bd9;
}

/* Ссылки на каналы */
.main-text :deep(.channel-link) {
  color: #0088cc;
}

.main-text :deep(.channel-link:hover) {
  color: #006699;
}

/* Внешние ссылки */
.main-text :deep(.external-link) {
  color: #0066cc;
}

.main-text :deep(.external-link:hover) {
  color: #0052cc;
}

/* Стили для блока ссылок */
.message-links {
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.links-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.links-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.link-item {
  color: #0066cc;
  text-decoration: none;
  font-size: 0.875rem;
  padding: 0.25rem 0;
  word-break: break-all;
  transition: color 0.2s ease;
}

.link-item:hover {
  color: #004499;
  text-decoration: underline;
}

.link-item i {
  font-size: 0.75rem;
  opacity: 0.7;
}
</style>